// App.js - Updated with Global Session Management
import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate, Outlet, useParams } from "react-router-dom";
import { SessionProvider } from "./context/SessionContext";
import GlobalSessionWarning from "./components/GlobalSessionWarning";
import DevToolsGuard from "./components/common/DevToolsGuard/DevToolsGuard";

// Your existing components - NO CHANGES NEEDED
import Layout from "./components/layout/Layout";
// import Login from "./components/Pages/Login";
import Login from "./components/Pages/Login";
import ForgotPassword from "./components/Pages/ForgotPassword";
import Dashboard from "./components/Pages/Dashboard/Dashboard";
import NotFound from "./components/Pages/NotFound";
import UserManagement from "./components/Pages/SuperAdmin/userManagement";
import CroStudyAssignment from "./components/Pages/SuperAdmin/CroStudyAssignment";
import UserProfile from "./components/Pages/SuperAdmin/userProfile";
import CommonComponent from "./components/Pages/SuperAdmin/commonComponents";
import Charts from "./components/common/chartexample/Charts";
import OnlyOffice from "./components/Pages/SuperAdmin/onlyOfficeEditor";
import QMSpreview from "./components/Pages/SuperAdmin/QMSpreview";
import OnlineOffice from "./components/Pages/SuperAdmin/onlineOffice";
import ISFDasboard from "./components/Pages/isf/isfdashboard";
import ISF from "./components/Pages/isf/isf";
import ISFTemplate from "./components/Pages/isf/isftemplate";
import MilestoneLibrary from "./components/Pages/isf/milestoneLibrary";
import MilestonePlan from "./components/Pages/isf/milestonePlan";
import FolderLibrary from "./components/Pages/isf/folderLibrary";
import RemovedDocuments from "./components/Pages/isf/removedDocuments";
import DownloadAuditLog from "./components/Pages/isf/DownloadAuditLog";
import IsfReports from "./components/Pages/isf/IsfReports";
import OrganizationSelector from "./components/Pages/OrganizationSelector ";
import StudyManagement from "./components/Pages/studymanagement/studymanagement";
import StudyProfile from "./components/Pages/studymanagement/StudyProfile";
import ScheduleEditor from "./components/Pages/schedule/ScheduleEditor";
import ParticipantList from "./components/Pages/participantmanager/ParticipantList";
import AddParticipant from "./components/Pages/participantmanager/AddParticipant";
import ParticipantProfile from "./components/Pages/participantmanager/ParticipantProfile";
import CampaignTemplateList from "./components/Pages/participantmanager/CampaignTemplateList";
import FeasibilityList from "./components/Pages/participantmanager/FeasibilityList";
import FeasibilityEditor from "./components/Pages/participantmanager/FeasibilityEditor";
import CommunicationWizard from "./components/Pages/participantmanager/CommunicationWizard";
import BulkEmailSms from "./components/Pages/participantmanager/BulkEmailSms";
import RoleManagement from "./components/Pages/SuperAdmin/rolemanagement";
import DocumentTypeManagement from "./components/Pages/SuperAdmin/DocumentTypeManagement";
import SiteManagement from "./components/Pages/SuperAdmin/SiteManagement";
import DepartmentManagement from "./components/Pages/SuperAdmin/DepartmentManagement";
import CapacityManagement from "./components/Pages/SuperAdmin/CapacityManagement";
import OrganizationSetup from "./components/Pages/SuperAdmin/OrganizationSetup";
import SuperAdminOrganisations from "./components/Pages/SuperAdmin/SuperAdminOrganisations";
import SuperAdminFaqManagement from "./components/Pages/SuperAdmin/SuperAdminFaqManagement";
import TermsAndConditionsAccept from "./components/Pages/CredentialPolicy/TermsAndConditionsAccept";
import RequiredCredentialChange from "./components/Pages/CredentialPolicy/RequiredCredentialChange";
import CapaConfigMaster from "./components/Pages/SuperAdmin/CapaConfigMaster";
import QmsConfigMaster from "./components/Pages/SuperAdmin/QmsConfigMaster";
import EisfConfigMaster from "./components/Pages/SuperAdmin/EisfConfigMaster";
import FAQ from "./components/Pages/FAQ/FAQ";
import LegalPolicies from "./components/Pages/LegalPolicies/LegalPolicies";
import SupportTickets from "./components/Pages/SupportTickets";
import CapaCreate from "./components/Pages/capa/CapaCreate";
import CapaDashboard from "./components/Pages/capa/CapaDashboard";
import CapaList from "./components/Pages/capa/CapaList";
import CapaDetail from "./components/Pages/capa/CapaDetail";
import CapaEdit from "./components/Pages/capa/CapaEdit";
import CapaTasks from "./components/Pages/capa/CapaTasks";
import CapaApprovals from "./components/Pages/capa/CapaApprovals";
import CapaEffectiveness from "./components/Pages/capa/CapaEffectiveness";
import CapaReports from "./components/Pages/capa/CapaReports";
import CapaConfig from "./components/Pages/capa/CapaConfig";
import CapaAudit from "./components/Pages/capa/CapaAudit";
import CapaRemovedList from "./components/Pages/capa/CapaRemovedList";
import {
  QmsDashboard,
  QmsComplianceReport,
  QmsMyVault,
  QmsPublishDocuments,
  QmsDocumentLibrary,
  QmsCreateChangeRequest,
  QmsChangeRequests,
  QmsDocumentControl,
  QmsReadingList,
  QmsQuestionBank,
  QmsQuizManager,
  QmsMyLearnings,
  QmsSharedWithMe,
} from "./components/Pages/qms/qmsPages";
import OrganizationEmailInbox from "./components/Pages/EmailPlatform/OrganizationEmailInbox";
import NotificationCenterInbox from "./components/Pages/Notifications/NotificationCenterInbox";
import { LoaderProvider } from "./context/LoaderContext";
import GlobalLoader from "./components/GlobalLoader";
import { getAuthItem } from "./services/authSessionStore";
import SponsorCroDashboard from "./components/Pages/SponsorCroDashboard";
import { getUserData } from "./services/apiService";
import { resolvePostLoginRoute } from "./utils/credentialGateUtils";

const EplannerAppointmentList = lazy(
  () => import("./components/Pages/eplanner/appointments/AppointmentList"),
);
const EplannerAppointmentCalendar = lazy(
  () => import("./components/Pages/eplanner/appointments/AppointmentCalendar"),
);
// Unified Appointment Changes (adhoc / cancelled / DNA / out-of-window / rescheduled)
const EplannerAppointmentChanges = lazy(
  () => import("./components/Pages/eplanner/appointments/AppointmentChanges"),
);
const EplannerVolunteerSearch = lazy(
  () => import("./components/Pages/eplanner/volunteers/VolunteerSearch"),
);
const EplannerVolunteerAppointments = lazy(
  () => import("./components/Pages/eplanner/volunteers/VolunteerAppointments"),
);
const EplannerLocationOccupancy = lazy(
  () => import("./components/Pages/eplanner/occupancy/LocationOccupancy"),
);
const EplannerAnalytics = lazy(
  () => import("./components/Pages/eplanner/analytics/EplannerAnalytics"),
);
const EplannerWhatsAppCommunication = lazy(
  () =>
    import(
      "./components/Pages/eplanner/communication/WhatsAppCommunication"
    ),
);

const EplannerRouteFallback = () => (
  <div className="p-3 text-muted">Loading E-Planner…</div>
);
// Enhanced authentication check (using secure session validation)
const isLoggedIn = () => {
  const isLoggedInFlag = getAuthItem("isLoggedIn");
  const sessionToken = getAuthItem("sessionToken");

  // Basic check - session context will handle detailed validation
  return isLoggedInFlag === "true" && sessionToken;
};

// Protected Route Wrapper - Enhanced
const ProtectedRoute = () => {
  return isLoggedIn() ? <Outlet /> : <Navigate to="/login" replace />;
};

const RedirectExternalUserProfile = () => {
  const { staffId } = useParams();
  return <Navigate to={`/userprofile/${staffId}`} replace />;
};

function App() {
  return (
    <LoaderProvider>
      <GlobalLoader />
      <SessionProvider>
        <div className="App">
          {/* Global session warning - shows automatically when needed */}
          <GlobalSessionWarning />
          {/* Soft DevTools block — toggle per env via apiconfig.js `blockDevTools` */}
          <DevToolsGuard />

          <Routes>
            {/* Root route */}
            <Route
              path="/"
              element={
                <Navigate
                  to={
                    isLoggedIn()
                      ? resolvePostLoginRoute(getUserData(), false)
                      : "/login"
                  }
                  replace
                />
              }
            />
            {/* Login Route - Public */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            {/* QMS Preview - Public (without login) */}
            <Route path="/qmspreview" element={<QMSpreview />} />
            {/* Legal Policies - Public (without login) */}
            <Route path="/legal-policies" element={<LegalPolicies />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/accept-terms" element={<TermsAndConditionsAccept />} />
              <Route path="/change-credentials-required" element={<RequiredCredentialChange forced />} />
            </Route>
            {/* <Route path="/login2" element={<Login2 />} /> */}
            {/* Protected Routes - ALL AUTOMATICALLY SECURED */}
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/dashboard" element={<Dashboard />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/usermanagement" element={<UserManagement />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route
                  path="/assign-studies-cro"
                  element={<CroStudyAssignment />}
                />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/userprofile/:staffId" element={<UserProfile />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route
                path="/externaluserprofile/:staffId"
                element={<RedirectExternalUserProfile />}
              />
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/rolemanagement" element={<RoleManagement />} />
              </Route>
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route
                  path="/documenttype"
                  element={<Navigate to="/superadmin/qms-config?section=documentTypes" replace />}
                />
                <Route
                  path="/qms/document-types"
                  element={<Navigate to="/superadmin/qms-config?section=documentTypes" replace />}
                />
              </Route>
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/qms/dashboard" element={<QmsDashboard />} />
                <Route
                  path="/qms/compliance-report"
                  element={<QmsComplianceReport />}
                />
                <Route
                  path="/qms/document-categories"
                  element={<Navigate to="/superadmin/qms-config?section=documentCategories" replace />}
                />
                <Route path="/qms/my-vault" element={<QmsMyVault />} />
                <Route path="/qms/publish" element={<QmsPublishDocuments />} />
                <Route path="/qms/library" element={<QmsDocumentLibrary />} />
                <Route
                  path="/qms/change-requests/create"
                  element={<QmsCreateChangeRequest />}
                />
                <Route
                  path="/qms/change-requests"
                  element={<QmsChangeRequests />}
                />
                <Route path="/qms/document-control" element={<QmsDocumentControl />} />
                <Route
                  path="/qms/tracker"
                  element={<Navigate to="/qms/document-control" replace />}
                />
                <Route
                  path="/qms/transfer-ownership"
                  element={<Navigate to="/qms/document-control" replace />}
                />
                <Route
                  path="/qms/archive"
                  element={<Navigate to="/qms/document-control" replace />}
                />
                <Route path="/qms/reading-list" element={<QmsReadingList />} />
                <Route
                  path="/qms/certificate-templates"
                  element={
                    <Navigate to="/superadmin/qms-config" replace />
                  }
                />
                <Route path="/qms/question-bank" element={<QmsQuestionBank />} />
                <Route path="/qms/quizzes" element={<QmsQuizManager />} />
                <Route path="/qms/my-learnings" element={<QmsMyLearnings />} />
                <Route path="/qms/my-learning" element={<QmsMyLearnings />} />
                <Route path="/qms/my-records" element={<QmsMyLearnings />} />
                <Route
                  path="/qms/shared-with-me"
                  element={<QmsSharedWithMe />}
                />
              </Route>
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route
                  path="/documentcategory"
                  element={<Navigate to="/superadmin/qms-config?section=documentCategories" replace />}
                />
              </Route>
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/sitemanagement" element={<SiteManagement />} />
                <Route path="/departments" element={<DepartmentManagement />} />
                <Route
                  path="/capacitymanagement"
                  element={<CapacityManagement />}
                />
                <Route path="/organizationsetup" element={<OrganizationSetup />} />
                <Route
                  path="/superadmin/organisations"
                  element={<SuperAdminOrganisations />}
                />
                <Route
                  path="/superadmin/faq-management"
                  element={<SuperAdminFaqManagement />}
                />
                <Route
                  path="/superadmin/login-ip-allowlist"
                  element={<Navigate to="/organizationsetup" replace />}
                />
              </Route>
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/superadmin/capa-config" element={<CapaConfigMaster />} />
                <Route path="/superadmin/qms-config" element={<QmsConfigMaster />} />
                <Route path="/superadmin/eisf-config" element={<EisfConfigMaster />} />
                <Route
                  path="/eisf/document-types"
                  element={<Navigate to="/superadmin/eisf-config?section=documentTypes" replace />}
                />
                <Route
                  path="/eisf/document-categories"
                  element={<Navigate to="/superadmin/eisf-config?section=documentCategories" replace />}
                />
              </Route>
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/commoncomponent" element={<CommonComponent />} />
              </Route>
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/charts" element={<Charts />} />
              </Route>
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/onlyoffice" element={<OnlyOffice />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/onlineoffice" element={<OnlineOffice />} />
              </Route>
            </Route>
             <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/isfdashboard" element={<ISFDasboard />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/isf" element={<ISF />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/shared-home" element={<SponsorCroDashboard />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/isftemplate" element={<ISFTemplate />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/milestone-library" element={<MilestoneLibrary />} />
                <Route
                  path="/milestone-library/:milestoneId"
                  element={<MilestoneLibrary />}
                />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/milestone-plan" element={<MilestonePlan />} />
                <Route path="/folder-library" element={<FolderLibrary />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route
                  path="/removeddocuments"
                  element={<RemovedDocuments />}
                ></Route>
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/isf/reports" element={<IsfReports />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route
                  path="/downloadauditlog"
                  element={<DownloadAuditLog />}
                ></Route>
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route path="/organizations" element={<OrganizationSelector />} />
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/studymanagement" element={<StudyManagement />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/studyprofile/:studyId" element={<StudyProfile />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/schedule/:studyId" element={<ScheduleEditor />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/participantmanager" element={<Navigate to="/participantmanager/active" replace />} />
                <Route path="/participantmanager/active" element={<ParticipantList />} />
                {/* Legacy inactive URL → unified Participants list */}
                <Route
                  path="/participantmanager/inactive"
                  element={<Navigate to="/participantmanager/active" replace />}
                />
                <Route path="/participantmanager/add" element={<AddParticipant />} />
                <Route path="/participantmanager/participant/:volunteerId/*" element={<ParticipantProfile />} />
                <Route path="/participantmanager/campaign-templates" element={<CampaignTemplateList />} />
                <Route
                  path="/participantmanager/archived-campaigns"
                  element={<Navigate to="/participantmanager/campaign-templates" replace />}
                />
                <Route path="/participantmanager/feasibility" element={<FeasibilityList />} />
                <Route path="/participantmanager/feasibility/new" element={<FeasibilityEditor />} />
                <Route path="/participantmanager/feasibility/:participantId/edit" element={<FeasibilityEditor />} />
                <Route path="/participantmanager/communication" element={<CommunicationWizard />} />
                <Route path="/participantmanager/bulk-email-sms" element={<BulkEmailSms />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/faq" element={<FAQ />} />
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/my-tickets" element={<SupportTickets />} />
              </Route>
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/capa/new" element={<CapaCreate />} />
                <Route path="/capa-dashboard" element={<CapaDashboard />} />
                <Route path="/capa/tasks" element={<CapaTasks />} />
                <Route path="/capa/approvals" element={<CapaApprovals />} />
                <Route path="/capa/effectiveness" element={<CapaEffectiveness />} />
                <Route path="/capa/reports" element={<CapaReports />} />
                <Route path="/capa/config" element={<CapaConfig />} />
                <Route path="/capa/audit" element={<CapaAudit />} />
                <Route path="/capa/removed" element={<CapaRemovedList />} />
                <Route path="/capa/:id/edit" element={<CapaEdit />} />
                <Route path="/capa/:id" element={<CapaDetail />} />
                <Route path="/capa" element={<CapaList />} />
              </Route>
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/eplanner" element={<Navigate to="/eplanner/appointments" replace />} />
                <Route path="/eplanner/book" element={<Navigate to="/eplanner/volunteers/search" replace />} />
                <Route
                  path="/eplanner/appointments"
                  element={
                    <Suspense fallback={<EplannerRouteFallback />}>
                      <EplannerAppointmentList />
                    </Suspense>
                  }
                />
                <Route
                  path="/eplanner/calendar"
                  element={
                    <Suspense fallback={<EplannerRouteFallback />}>
                      <EplannerAppointmentCalendar />
                    </Suspense>
                  }
                />
                <Route
                  path="/eplanner/appointments/changes"
                  element={
                    <Suspense fallback={<EplannerRouteFallback />}>
                      <EplannerAppointmentChanges />
                    </Suspense>
                  }
                />
                <Route
                  path="/eplanner/appointments/cancelled"
                  element={<Navigate to="/eplanner/appointments/changes" replace />}
                />
                <Route
                  path="/eplanner/appointments/out-of-window"
                  element={<Navigate to="/eplanner/appointments/changes" replace />}
                />
                <Route
                  path="/eplanner/volunteers/search"
                  element={
                    <Suspense fallback={<EplannerRouteFallback />}>
                      <EplannerVolunteerSearch />
                    </Suspense>
                  }
                />
                <Route
                  path="/eplanner/volunteers/appointments"
                  element={
                    <Suspense fallback={<EplannerRouteFallback />}>
                      <EplannerVolunteerAppointments />
                    </Suspense>
                  }
                />
                <Route
                  path="/eplanner/occupancy"
                  element={
                    <Suspense fallback={<EplannerRouteFallback />}>
                      <EplannerLocationOccupancy />
                    </Suspense>
                  }
                />
                <Route
                  path="/eplanner/analytics"
                  element={
                    <Suspense fallback={<EplannerRouteFallback />}>
                      <EplannerAnalytics />
                    </Suspense>
                  }
                />
                <Route
                  path="/eplanner/communication"
                  element={
                    <Suspense fallback={<EplannerRouteFallback />}>
                      <EplannerWhatsAppCommunication />
                    </Suspense>
                  }
                />
              </Route>
            </Route>

            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/notification-center" element={<NotificationCenterInbox />} />
                <Route path="/organization-email-inbox" element={<OrganizationEmailInbox />} />
                <Route
                  path="/email-platform-sample"
                  element={<Navigate to="/organization-email-inbox" replace />}
                />
              </Route>
            </Route>

            {/* Not Found */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </SessionProvider>
    </LoaderProvider>
  );
}
export default App;
